import { FC, useEffect, useMemo, useState } from 'react';
import { Collapse, Tag } from 'antd';
import FieldCheckbox from 'components/FormBuilder/fields/FieldCheckbox';
import { FormBuilderData, FormBuilderFieldCheckbox } from 'types/formBuilder';
import { useChecklistLogic } from './useChecklistLogic';

const { Panel } = Collapse;

const getGroupHeader = (group: FormBuilderFieldCheckbox, index: number, hasMoreThanOneGroup: boolean) => {
  return hasMoreThanOneGroup ? `Step ${index + 1}: ${group.label}` : group.label;
};

interface ChecklistSectionProps {
  group: FormBuilderFieldCheckbox;
  status?: { isComplete: boolean; isDisabled: boolean };
  onQuestionChange: (groupName: string, questionValue: string[]) => void;
  headerContent?: React.ReactNode;
  readOnly?: boolean;
}

const ChecklistSection: FC<ChecklistSectionProps> = ({ group, onQuestionChange, readOnly = false }) => {
  return (
    <FieldCheckbox
      key={`section-${group.name}`}
      data={group as any}
      disabled={readOnly}
      value={group.answer as string[]}
      onChange={(value) => {
        onQuestionChange(group.name, value as string[]);
      }}
      checklist
    />
  );
};

interface ChecklistProps {
  checklist: FormBuilderData;
  taskStatus: string;
  onChange: (newChecklistData: FormBuilderData) => void;
  readOnly?: boolean;
}

const Checklist: FC<ChecklistProps> = ({ checklist, onChange, readOnly = false }) => {
  const groupQuestions = useChecklistLogic(checklist);

  const [activeKey, setActiveKey] = useState<string[]>([]);

  const allGroupKeys = useMemo(() => {
    return groupQuestions.map((group) => `${checklist.id}-${group.name}`);
  }, [groupQuestions]);

  const allGroupsAreOpen = activeKey.length === allGroupKeys.length;

  const handleShowAllGroups = () => {
    if (allGroupsAreOpen) {
      setActiveKey([]);
      return;
    }

    setActiveKey(allGroupKeys);
  };

  const handleQuestionChange = (groupName: string, questionValue: string[]) => {
    const foundGroup = groupQuestions.find((group) => group.name === groupName);
    if (!foundGroup) return;

    const updatedGroup = {
      ...foundGroup,
      answer: questionValue,
    };

    const updatedForm = (() => {
      const exists = checklist.form?.some((item) => 'name' in item && item.name === groupName);

      if (!exists) {
        return [...(checklist.form ?? []), updatedGroup];
      }

      return checklist.form!.map((item) =>
        'name' in item && item.name === groupName ? { ...item, answer: updatedGroup.answer } : item,
      );
    })();

    const newChecklist = {
      ...checklist,
      form: updatedForm,
    };

    onChange(newChecklist as FormBuilderData);
  };

  useEffect(() => {
    setActiveKey([]);
  }, []);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tag color="blue" style={{ cursor: 'pointer', marginBottom: 10, marginRight: 0 }} onClick={handleShowAllGroups}>
          {allGroupsAreOpen ? 'Collapse all' : 'Expand all'}
        </Tag>
      </div>
      <Collapse
        className="custom-checklist-collapse"
        activeKey={activeKey}
        onChange={(key) => {
          if (Array.isArray(key)) {
            setActiveKey(key);
          } else {
            setActiveKey(key ? [String(key)] : []);
          }
        }}
      >
        {groupQuestions.map((group, index) => {
          const hasMoreThanOneGroup = groupQuestions.length > 1;
          const hasGroupStatusLock = Boolean(group.statusLock?.length);
          const hasGroupBeenCompleted = group.answer && group.answer.length === group.options.length;

          const backgroundColor = (() => {
            if (!hasGroupStatusLock) return 'transparent';
            if (hasGroupBeenCompleted) return 'transparent';
            return 'rgba(255, 0, 0, .1)';
          })();

          return (
            <Panel
              key={`${checklist.id}-${group.name}`}
              header={getGroupHeader(group, index, hasMoreThanOneGroup)}
              style={{ backgroundColor }}
            >
              <ChecklistSection key={group.name} group={group} onQuestionChange={handleQuestionChange} readOnly={readOnly} />
            </Panel>
          );
        })}
      </Collapse>
    </>
  );
};

export default Checklist;
